import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AppState } from '../store/state';
import { selectCurrentRole, selectCurrentUser, selectSubmissionState, selectTrackRoles } from '../store/selectors';
import { connect } from 'react-redux';
import { AppDispatch } from '../store/store';
import { Submission } from '../store/submission/types';
import Loading from '../components/Loading/Loading';
import { Role } from '../store/conference/types';
import { User } from '../store/user/types';
import { Link } from '../components/ui';
import TextAreaLatex, { emptyAnswer } from '../components/TextAreaLatex/TextAreaLatex';
import { Message, MessageDTO, Participant } from '../store/discussion/types';
import { deleteMessage, editMessage, getDiscussion, postMessage, subscribe } from '../store/discussion/sagas';
import { AnswerJson } from '../store/form/types';
import submissionSlice from '../store/submission/slice';
import styled, { ThemeProvider } from 'styled-components';
import useTheme from '@material-ui/core/styles/useTheme';
import { Menu, MenuItem, MenuSelectEvent } from '@progress/kendo-react-layout';
import moment from 'moment';
import { getUserOffset } from '../helpers/timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faPaperPlane,
  faRectangleXmark,
  faSquareCaretUp,
  faFloppyDisk,
} from '@fortawesome/free-solid-svg-icons';
import { can, SubmissionPermissionManager } from '../helpers/permissions';
import TextAreaDisplay from '../components/TextAreaDisplay/TextAreaDisplay';
import { OpenSnackBarDTO } from '../store/error/types';
import errorSlice from '../store/error/slice';
import { useTranslation } from 'react-i18next';
import { getUserAndRoleDisplayName } from '../helpers/users';
import BackButton from '../components/ui/buttons/BackButton/BackButton';
import { useIsMobile } from '../helpers/window';
import { Button, CancelButton } from '../components/ui';
import { goBackFromEditReview } from './ReviewEditPage';

interface Props {
  loading: boolean;
  submissionsById: { [key: number]: Submission };
  roleById: { [key: string]: Role };
  getSubmissionsAction: () => void;
  user: User;
  timezone: string | null;
  roleId?: number;
  openSnackBarAction: (data: OpenSnackBarDTO) => void;
}

interface Path {
  nodeId: number;
  depth: number;
}

const ROOT_NODE_ID = 0;

class Graph {
  adjacencyList: { [key: number]: number[] } = {};

  constructor(messages: Message[]) {
    messages.forEach((message) => {
      const parentId = message.reply_to_id ?? ROOT_NODE_ID;
      if (!(parentId in this.adjacencyList)) {
        this.adjacencyList[parentId] = [];
      }
      this.adjacencyList[parentId].push(message.id);
    });
  }

  public dfs() {
    const path: Path[] = [];

    this._dfs(ROOT_NODE_ID, 0, path);

    path.shift(); // Remove first element
    return path;
  }

  protected _dfs(nodeId: number, depth = 0, path: any[]) {
    path.push({ nodeId, depth });
    this.neighbors(nodeId).forEach((nodeId) => this._dfs(nodeId, depth + 1, path));
  }

  protected neighbors(nodeId: number): number[] {
    return this.adjacencyList[nodeId] ?? [];
  }
}

const DiscussionPage: React.FC<Props> = ({
  loading,
  submissionsById,
  roleById,
  getSubmissionsAction,
  user,
  timezone,
  roleId,
  openSnackBarAction,
}: Props) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [participants, setParticipants] = useState<{ [key: string]: Participant }>({});
  const [messageLoading, setMessageLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [selectedNodeId, setSelectedNodeId] = useState<number | undefined>(undefined);
  const [body, setBody] = useState<AnswerJson>(emptyAnswer);
  const [newMessageBody, setNewMessageBody] = useState<AnswerJson>(emptyAnswer);
  const [editMode, setEditMode] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const [mobileFooterButtons, setMobileFooterButtons] = useState<boolean>(true);
  const [showMobileTextArea, setShowMobileTextArea] = useState(false);

  const params: any = useParams();
  const { submissionId: submission_external_id } = params;

  const theme = useTheme();

  const submission = Object.values(submissionsById).find(
    (submission) => submission.external_id == submission_external_id,
  );

  useEffect(() => {
    if (Object.keys(submissionsById).length === 0) {
      getSubmissionsAction();
    } else if (submission) {
      setMessageLoading(true);
      getDiscussion(submission.id).then((response) => {
        if (response.type == 'ok') {
          const { participants, messages, subscribed } = response.value.data;

          const byKey: { [key: string]: Participant } = {};
          participants.forEach((participant) => {
            byKey[participant.person_role_id] = participant;
          });
          setParticipants(byKey);

          setMessages(messages);
          setIsSubscribed(subscribed);

          setMessageLoading(false);
          scrollTo('bottom');
        }
      });
    }
  }, [Object.keys(submissionsById).length]);

  useEffect(() => {
    if (selectedNodeId) {
      scrollTo('message');
    }
  }, [selectedNodeId]);

  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const messagesStartRef = useRef<null | HTMLDivElement>(null);
  const messageRef = useRef<null | HTMLDivElement>(null);

  const isNewMessage = newMessageBody.value.length > 0;

  const resetMessageState = () => {
    setSelectedNodeId(undefined);
    isNewMessage ? setNewMessageBody(emptyAnswer()) : setBody(emptyAnswer());
    setShowMobileTextArea(false);
    setMobileFooterButtons(true);
    setEditMode(false);
  };

  /**
   * behavior: smooth | instant | auto
   * block (vertical alignment, defaults to start): start | center | end | nearest
   * inline (horizontal alignment, defaults to nearest): start | center | end | nearest
   */
  const scrollTo = (position: string) => {
    switch (position) {
      case 'bottom':
        if (messagesEndRef.current != null) {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
        break;

      case 'message':
        if (messageRef.current != null) {
          messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;

      case 'top':
      default:
        if (messagesStartRef.current != null) {
          messagesStartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        break;
    }
  };

  const saveHandler = () => {
    if ((newMessageBody.value.length > 0 || body.value.length > 0) && submission) {
      const data: MessageDTO = {
        id: 0,
        submission_id: submission.id,
        reply_to_id: selectedNodeId == ROOT_NODE_ID || selectedNodeId == undefined ? null : selectedNodeId,
        body: isNewMessage ? newMessageBody : body,
      };
      postMessage(data).then((response) => {
        if (response.type == 'ok') {
          const newMessage = response.value.data;
          setMessages([...messages, newMessage]);
          resetMessageState();

          if (!selectedNodeId && !editMode) {
            scrollTo('bottom'); // Just scroll to bottom if it a message entered via bottom text area
          }
        }
      });
    }
  };

  const editHandler = () => {
    if (selectedNodeId) {
      editMessage({
        id: selectedNodeId,
        submission_id: 0,
        reply_to_id: null,
        body,
      }).then((response) => {
        if (response.type == 'ok') {
          setMessages(
            messages.map((message) => {
              return {
                ...message,
                body: selectedNodeId == message.id ? body : message.body,
                updated_at: selectedNodeId == message.id ? new Date().toISOString() : message.updated_at,
              };
            }),
          );
          setEditMode(false);
          setBody(emptyAnswer());
          setSelectedNodeId(undefined);
        }
      });
    }
  };

  if (loading || messageLoading || !roleId) {
    return <Loading />;
  }

  if (!submission) {
    return <div>{t('Submission #{{externalId}} not found', { externalId: submission_external_id })}</div>;
  }

  // Visibility permissions
  const userRole = roleById[roleId];
  const isChair = userRole.type == 'chair';
  const spm = new SubmissionPermissionManager(submission);
  const canViewDiscussion = spm.canViewDiscussion(user, userRole);
  const discussionIsOpen = spm.canEditDiscussion();
  const canModerate = can('IS_MODERATOR_OF_DISCUSSION') || isChair;
  const canSubscribe = userRole.type != 'author';

  /*
    If you are a reviewer and you are not assigned to the paper of the discussion that you want to access, appears the message "Submission not found".
    So, it's not necessary additional control to access related with this topic like "isReviewerAssigned" in the SubmissionDetailPage.
  */

  if (!canViewDiscussion) {
    return <div>{t('This discussion is not active.')}</div>;
  }

  const graph = new Graph(messages);

  const messageById: { [key: number]: Message } = {};
  messages.forEach((message) => {
    messageById[message.id] = message;
  });

  // Textarea that it's showed when you REPLIES a message
  const textArea = (
    <div ref={messageRef}>
      <TextAreaLatex
        value={body?.value}
        format={body?.format}
        onChange={(data) => (showMobileTextArea ? setNewMessageBody(data) : setBody(data))}
        rows={2}
      />
      <div className="mt-1 text-right">
        <Link
          className="cursor-pointer text-sm"
          onClick={() => {
            resetMessageState();
          }}
        >
          <FontAwesomeIcon icon={faRectangleXmark} size="xl" />
          <span className="ml-1">{t('Cancel')}</span>
        </Link>
        <Link className="ml-3 cursor-pointer text-sm" onClick={() => saveHandler()}>
          <FontAwesomeIcon icon={faPaperPlane} size="lg" />
          <span className="ml-1">{t('Send')}</span>
        </Link>
      </div>
    </div>
  );

  const actionsLateralMenu = (
    <StyledSubmenu className="mobileCtrlDiscussionActions">
      <>
        <div>
          <h2>{t('Actions')}</h2>
          <ul>
            <li>
              <Link
                onClick={() =>
                  subscribe(submission.id).then((response) => {
                    if (response.type == 'ok') {
                      const { subscribed } = response.value.data;
                      setIsSubscribed(subscribed);
                      openSnackBarAction({
                        message: `${
                          subscribed
                            ? t('You will now receive email notifications for this discussion.')
                            : t('You will no longer receive email notifications for this discussion.')
                        }`,
                        severity: 'success',
                      });
                    }
                  })
                }
              >
                {isSubscribed ? t('Turn off email notifications') : t('Get email notifications')}
              </Link>
            </li>
          </ul>
        </div>
      </>
    </StyledSubmenu>
  );

  const messagesByPerson: Message[] = messages.filter((message) => message.person_id == user.person.id);
  const lasteMessageByPersonId: Message | undefined = messagesByPerson[messagesByPerson.length - 1];

  const authorParticipants: Participant[] = [];
  const reviewerParticipants: Participant[] = [];
  Object.values(participants).forEach((participant) => {
    const role = roleById[participant.role_id];
    switch (role.type) {
      case 'author':
        authorParticipants.push(participant);
        break;
      case 'reviewer':
        reviewerParticipants.push(participant);
        break;
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <StyledDiscussionContent ref={messagesEndRef}>
        <div className="flex flex-row discussion mb-14">
          <div className="w-8/12 mobileCtrlDiscussionPageHeader">
            {/** PAGE HEADER */}
            {!isMobile && (
              <div ref={messagesStartRef}>
                <BackButton onClick={() => history.goBack()} />
              </div>
            )}
            <h2 className="text-xl mb-8 font-bold text-gray-700">
              {t('Discussion for Paper')} #{submission.external_id}: {submission.title}
            </h2>
            {isMobile && canSubscribe && actionsLateralMenu}
            {authorParticipants.length + reviewerParticipants.length > 0 ? (
              <div>
                <span className="mr-2 font-semibold">{t('Participants') + ':'}</span>
                {authorParticipants.length > 0 && (
                  <div key="author">
                    <span>
                      {getUserAndRoleDisplayName(
                        userRole,
                        roleById[authorParticipants[0].role_id],
                        authorParticipants[0].full_name,
                        userRole.type == 'author',
                        t,
                        authorParticipants[0].external_id,
                      )
                        .join(' ')
                        .trim()}
                    </span>
                  </div>
                )}
                {reviewerParticipants.map((participant, index) => {
                  const role = roleById[participant.role_id];
                  return (
                    <div key={index}>
                      <span>
                        {getUserAndRoleDisplayName(
                          userRole,
                          role,
                          participant.full_name,
                          participant.person_id == user.person.id,
                          t,
                          participant.external_id,
                        )
                          .join(' ')
                          .trim()}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>{t("There aren't any participants in this discussion.")}</div>
            )}
            <div className="flex h-full flex-col mt-5">
              {graph.dfs().length > 0 ? (
                <div className="mt-3 ml-2 grow h-full mobileCtrlDiscussionWrapper">
                  {graph.dfs().map((path) => {
                    const { nodeId, depth } = path;
                    const { person_role_id, role_id, body: messageBody, updated_at, created_at } = messageById[nodeId];
                    const role = roleById[role_id];
                    const participant: Participant = participants[person_role_id] ?? {
                      person_id: 0,
                      person_role_id: 0,
                      role_id,
                      full_name: t('Anonymous'),
                      external_id: 0,
                    };
                    const isSelectedNode = selectedNodeId === nodeId;

                    const utcOffset = getUserOffset(timezone);
                    const reverse = (date: Date) => {
                      const dateStr = moment(date).format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS) + utcOffset;
                      return new Date(dateStr).toLocaleString();
                    };
                    const dateCreatedUtcOffset = reverse(new Date(created_at));
                    const dateUpdatedUtcOffset = reverse(new Date(updated_at));

                    const editLastMessage = discussionIsOpen && lasteMessageByPersonId?.id == nodeId;
                    const showReplyTextArea = discussionIsOpen && isSelectedNode && !editMode;
                    const readOnly = !(editMode && lasteMessageByPersonId?.id == nodeId);
                    const showFormatButtons = editMode;
                    const modifiedMessage = dateCreatedUtcOffset !== dateUpdatedUtcOffset;
                    const isYou =
                      (userRole.type == 'author' && role.type == 'author') || participant.person_id == user.person.id;

                    const handleSelect = (e: MenuSelectEvent) => {
                      if (e.item.text == 'Reply') {
                        resetMessageState();
                        setSelectedNodeId(nodeId);
                      }

                      if (e.item.text == 'Delete') {
                        deleteMessage(nodeId).then((response) => {
                          if (response.type == 'ok') {
                            setMessages(messages.filter((message) => message.id != nodeId));
                          }
                        });
                      }
                    };

                    return (
                      <div className={`relative ${depth - 1 == 0 ? 'pt-4' : ''}`} key={nodeId}>
                        {depth - 1 == 0 && <StyledHr className="absolute top-0" />}
                        <div className={`mobileCtrlDiscussionBox ml-${(depth - 1) * 12}`}>
                          <StyledMessage
                            className={`message-${depth} max-w-full w-max p-3 rounded-xl p-6 min-h-20 shadow-lg shadow-black border-gray-400 mobileCtrlDiscussionBoxMessage ${
                              isYou
                                ? 'bg-blue-300 mobileCtrlDiscussionBoxMessageYouColor'
                                : 'bg-gray-200 mobileCtrlDiscussionBoxMessageOtherColor'
                            }`}
                          >
                            {/**
                             * MESSAGE HEADER
                             * Show only ReviewerId if sender of message is not Chair
                             * Name of chair always visible.
                             * Name of sender is visible if is current user or has privileges.
                             * */}
                            <p className="text-xs mb-2">
                              {dateUpdatedUtcOffset} {modifiedMessage && <span className="ml-1">[edited]</span>}
                            </p>
                            <p className="font-bold mb-2">
                              {getUserAndRoleDisplayName(
                                userRole,
                                role,
                                participant.full_name,
                                isYou,
                                t,
                                participant.external_id,
                              )
                                .join(' ')
                                .trim()}
                            </p>

                            <StyledTextAreaLatex>
                              {/** DROPDOWN ACTIONS MENU */}
                              <div className="kmenu">
                                <Menu onSelect={handleSelect} openOnClick={true}>
                                  <MenuItem icon="more-vertical">
                                    {discussionIsOpen && <MenuItem text={t('Reply')} />}
                                    {canModerate && <MenuItem text={t('Delete')} />}
                                  </MenuItem>
                                </Menu>
                              </div>

                              {/**
                               * MESSAGE CONTENT
                               * By default (when it's not edit mode ) disabled is true
                               * and showFormatButtons is false
                               */}
                              {readOnly ? (
                                <TextAreaDisplay value={messageBody.value} format={messageBody.format ?? 'plain'} />
                              ) : (
                                <div ref={messageRef}>
                                  {/**
                                   * Textarea that it's showed when you EDIT a message
                                   * */}
                                  <TextAreaLatex
                                    value={body?.value}
                                    format={body?.format}
                                    showFormatButtons={showFormatButtons}
                                    onChange={(data) => setBody(data)}
                                    rows={5}
                                  />
                                </div>
                              )}

                              {/** EDIT BUTTON IN LAST MESSAGE USER */}
                              {discussionIsOpen && editLastMessage && !editMode && (
                                <Link
                                  className="cursor-pointer text-sm block absolute right-3 bottom-4"
                                  onClick={() => {
                                    resetMessageState();
                                    setBody(messageBody);
                                    setSelectedNodeId(nodeId);
                                    setEditMode(true);
                                  }}
                                >
                                  <StyledEditButton>
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                  </StyledEditButton>
                                  <span className="ml-1">{t('Edit')}</span>
                                </Link>
                              )}

                              {/** EDITION BUTTONS */}
                              {editLastMessage && editMode && (
                                <div className="mt-1 text-right">
                                  <Link
                                    className="cursor-pointer text-sm"
                                    onClick={() => {
                                      setEditMode(false);
                                      setBody(emptyAnswer());
                                      setSelectedNodeId(undefined);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faRectangleXmark} size="xl" />
                                    <span className="ml-1">{t('Cancel')}</span>
                                  </Link>
                                  <Link
                                    className="ml-3 cursor-pointer text-sm"
                                    onClick={() => {
                                      editHandler();
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faFloppyDisk} size="xl" />
                                    <span className="ml-1">{t('Save')}</span>
                                  </Link>
                                </div>
                              )}
                            </StyledTextAreaLatex>
                          </StyledMessage>

                          {showReplyTextArea && textArea}
                          <div className="h-5"></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="relative">
                  <StyledHr className="absolute top-0" />
                  <div className="pt-4">{t('There are no messages in this discussion yet.')}</div>
                </div>
              )}
            </div>
          </div>

          {/* SUBMENU */}
          {!isMobile && canSubscribe && actionsLateralMenu}
        </div>

        {/* TEXTAREA NEW MESSAGE */}
        {isMobile && mobileFooterButtons && (
          <>
            {/* FOOTER BUTTONS */}
            <StyledButtonsWrapper className="mobileCtrlReviewEditButtonsWrapper">
              <CancelButton onClick={() => goBackFromEditReview(submission_external_id)}>{t('Back')}</CancelButton>
              <Button
                className="w-48"
                onClick={() => {
                  resetMessageState();
                  setMobileFooterButtons(false);
                  setShowMobileTextArea(true);
                }}
              >
                {t('New post')}
              </Button>
            </StyledButtonsWrapper>
          </>
        )}

        {isMobile && showMobileTextArea && <div className="mobileCtrlTextAreaDiscussion">{textArea}</div>}

        {!isMobile && (
          <div className="new-message relative mobileCtrlInputMessageWrapper">
            {discussionIsOpen && (
              <>
                <div className={`${selectedNodeId ? 'opacityLayer' : ''}`}></div>
                {/**
                 * Textarea that it's showed when you CREATES A NEW MESSSAGE
                 */}
                <TextAreaLatex
                  value={newMessageBody?.value}
                  format={newMessageBody?.format}
                  onChange={(data) => setNewMessageBody(data)}
                  rows={4}
                />
                <div className="mt-1 text-right mobileCtrlDiscussionMessageSendIcon">
                  <Link
                    className="text-sm cursor-pointer block mb-2"
                    onClick={() => {
                      if (!selectedNodeId) {
                        saveHandler();
                      }
                    }}
                  >
                    <div>
                      <FontAwesomeIcon icon={faPaperPlane} size="lg" />
                      <span className="pl-2">{t('Send')}</span>
                    </div>
                  </Link>
                </div>
                <div className="text-sm absolute top-1 right-0.5">
                  <Link className="cursor-pointer" onClick={() => scrollTo('top')} title={t('Go to top')}>
                    <FontAwesomeIcon icon={faSquareCaretUp} size="2xl" />
                  </Link>
                </div>
              </>
            )}
          </div>
        )}
      </StyledDiscussionContent>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectSubmissionState(state).loading,
  submissionsById: selectSubmissionState(state).submissionsById,
  roleById: selectTrackRoles(state),
  user: selectCurrentUser(state),
  timezone: selectCurrentUser(state).person.timezone,
  roleId: selectCurrentRole(state)?.id,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getSubmissionsAction: () => dispatch(submissionSlice.actions.GET_SUBMISSIONS()),
  openSnackBarAction: (data: OpenSnackBarDTO) => dispatch(errorSlice.actions['OPEN:SNACKBAR'](data)),
});

const StyledHr = styled.hr`
  border-bottom: 2px dotted #d2d4d7;
  top: 0;
  width: 100%;
  z-index: 0;
  border-top: 0;
  position: absolute;
`;

const StyledTextAreaLatex = styled.div`
  > div {
    p {
      background-color: transparent !important;
    }
  }

  .kmenu .k-menu:not(.k-context-menu) > .k-item {
    color: #000;
  }

  .kmenu .k-menu-expand-arrow {
    margin: 10px 0 0 0;
  }

  .kmenu .k-i-caret-alt-down {
    display: none;
  }

  .kmenu .k-i-caret-alt-down::before {
    color: #000;
    content: '';
  }

  .kmenu {
    position: absolute;
    top: 10px;
    right: -5px;
  }
`;

const StyledDiscussionContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .discussion {
    flex-grow: 1;
  }

  .textAreaContent {
    background-color: transparent !important;
    padding: 0;
  }

  .textAreaContentWrapper {
    background-color: rgb(229 231 235);
    padding: 1rem;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  .new-message {
    position: sticky;
    background-color: #edf3ff;
    bottom: 0;
    z-index: 10;
    width: 100%;
    border-bottom: 0;
    border-left: 0;
    border-right: 0;
    padding-top: 0.5rem;
  }

  .opacityLayer {
    width: 100%;
    height: 75%;
    opacity: 0.7;
    position: absolute;
    background-color: #edf3ff;
    z-index: 1000;
    top: 0;
    left: 0;
  }
`;

const StyledMessage = styled.div`
  margin-bottom: 0.5rem;
  position: relative;
  min-width: 15rem;
`;

const StyledSubmenu = styled.section`
  position: sticky;
  top: 0;
  align-self: flex-start;
  padding-right: 2rem;
  padding-left: 8rem;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1.75rem;
  }

  ul {
    color: ${(props) => props.theme.palette.text.hint};

    li {
      margin-bottom: 0.5rem;

      a {
        cursor: pointer;
      }
    }
  }

  > ul {
    border-left: 2px solid ${(props) => props.theme.palette.text.hint};
    padding-left: 1rem;

    ul {
      margin-top: 0.5rem;
      padding-left: 1.875rem;
    }
  }
`;

const StyledEditButton = styled.span`
  padding: 0.2rem 0.3rem 0.2rem 0.4rem;
  background-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 1rem;
  color: #fff;
`;

const StyledButtonsWrapper = styled.div`
  position: absolute;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  bottom: 0;
  padding: 1rem 0;
  background-color: #fff;
  border-top: 1px dashed ${(props) => props.theme.palette.secondary.dark};

  .cancelButton {
    color: ${(props) => props.theme.palette.primary.main} !important;
  }
`;

export default connect(mapStateToProps, mapDispatchToProps)(DiscussionPage);
